import { useQuery, useReactiveVar } from '@apollo/client'
import { GetProjectNoteContentItemIdsQuery } from 'api/graphQL/queries/project.queries'
import { setElementLevelNotesCount, setElementNotesCount, workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { TASK_STATE_KEY, syncTaskContentItemIds } from 'api/graphQL/reactiveVars/workspaceTasks'
import { ProjectElementNodeWithMetadata } from 'api/graphQL/types/projectElement.types'
import { useEffect } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'

interface UseProjectTasksSyncOptions {
    projectId?: string
    selectedProjectNode?: ProjectElementNodeWithMetadata | null
}

interface UseProjectTasksSyncResult {
    isProjectTasksEnabled: boolean
    taskCount: number
}

/** Syncs the task-indicator content-item IDs and exposes the badge count for the Tasks button. */
export const useProjectTasksSync = ({
    projectId,
    selectedProjectNode,
}: UseProjectTasksSyncOptions): UseProjectTasksSyncResult => {
    const { previewFeatures } = usePreviewFeatures()
    const isProjectTasksEnabled = previewFeatures.hasProjectTasks
    const taskCount = useReactiveVar(workspaceSelectedElement).elementNotesCount
    const selectedElementId = selectedProjectNode?.elementId

    // Initial load — fetch note IDs for the current element so indicators appear on page open.
    // Apollo auto-refetches when selectedElementId changes (variable change triggers re-query).
    useQuery<{ getProject: { projectNotes: { id: string; contentItemId?: string }[] } }>(
        GetProjectNoteContentItemIdsQuery,
        {
            variables: {
                projectId,
                projectNoteSearchInput: {
                    filter: selectedElementId ? { elementId: selectedElementId } : {},
                    limit: 500,
                    skip: 0,
                },
            },
            skip: !isProjectTasksEnabled || !projectId,
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                const notes = data?.getProject?.projectNotes ?? []
                syncTaskContentItemIds(notes)
                setElementNotesCount(notes.length)
                setElementLevelNotesCount(notes.filter((n) => !n.contentItemId).length)
            },
        }
    )

    // After the task window performs any CRUD operation it broadcasts the current notes
    // state via localStorage. Consume it here to update reactive vars without a refetch.
    // No elementId check: element A's content-item IDs never collide with element B's node
    // keys, so there are no false-positive indicators. Counts may briefly reflect the task
    // window's element scope, but are corrected the next time useQuery fires on navigation.
    useEffect(() => {
        if (!isProjectTasksEnabled || !projectId) return
        const onStorage = (e: StorageEvent) => {
            if (e.key !== TASK_STATE_KEY) return
            try {
                const payload = e.newValue ? JSON.parse(e.newValue) : null
                if (payload?.projectId !== projectId) return
                const ids: string[] = Array.isArray(payload.contentItemIds) ? payload.contentItemIds : []
                syncTaskContentItemIds(ids.map((id) => ({ contentItemId: id })))
                setElementNotesCount(Number(payload.totalCount ?? 0))
                setElementLevelNotesCount(Number(payload.elementLevelCount ?? 0))
            } catch {
                // ignore malformed payload
            }
        }
        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [isProjectTasksEnabled, projectId])

    return { isProjectTasksEnabled, taskCount }
}
